<template>
    <div class="multi-select-dropdown" ref="dropdown">
        <div
            class="input-wrapper"
            :class="{ disabled: disabled }"
            @click="toggleDropdown"
            :tabindex="`${disabled ? -1 : 0}`"
            @keydown.enter="handleKeyDown"
        >
            <div class="chips-container">
                <span class="chip" v-for="option in selectedOptionObjects" :key="option.value">
                    <span class="chip-label" :title="option.label">{{ option.label }}</span>
                    <span class="chip-remove">
                        <img
                            class="theme-svg"
                            src="../../../public/icons/models/close.svg"
                            @click="removeOption(option.value)"
                        />
                    </span>
                </span>
                <span v-if="!selectedOptions.length" class="placeholder"> {{ localizer.Localize("Select") }} </span>
            </div>
            <span class="arrow">
                <f-icon icon="chevrondown" color="var(--neutral-manatee)" />
            </span>
        </div>

        <!-- Dropdown menu -->
        <Teleport to="body">
            <div v-if="isOpen" class="dropdown-menu" ref="teleportDropdown" :style="dropdownPosition">
                <input
                    type="text"
                    v-model="searchQuery"
                    :placeholder="localizer.Localize('Search_PascalCase')"
                    class="search-input"
                />
                <div v-if="filteredOptions.length">
                    <label v-for="option in filteredOptions" :key="option.value" class="dropdown-option">
                        <input type="checkbox" :value="option.value" v-model="selectedOptions" />
                        {{ option.label }}
                    </label>
                </div>
                <div v-else class="no-results">{{ localizer.Localize("NoResultsFound") }}</div>
            </div>
        </Teleport>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { Localizer } from "@/api/Localizer";
import { IMultiSelectDropdown } from "../pages/landscape/LandscapeManagementInterfaces";

export default defineComponent({
    data() {
        return {
            localizer: new Localizer(this.$store),
            isOpen: false,
            selectedOptions: [...this.modelValue],
            searchQuery: "",
            dropdownPosition: {
                position: "absolute",
                top: "0px",
                left: "0px",
                width: "0px",
                zIndex: 9999,
            },
        };
    },
    props: {
        options: {
            type: Array<IMultiSelectDropdown>,
            required: true,
        },
        modelValue: {
            type: Array<string | number>,
            default: () => [],
        },
        disabled: Boolean,
    },
    computed: {
        filteredOptions() {
            if (!this.searchQuery.trim()) {
                return this.options;
            }
            return this.options.filter((opt) => opt.label?.toLowerCase()?.includes(this.searchQuery?.toLowerCase()));
        },
        selectedOptionObjects() {
            // Compared as strings because v-model="selectedOptions" on the checkboxes matches
            // loosely, so a numeric option can already be selected via a string id.
            const selectedKeys = new Set((this.selectedOptions ?? []).map((value) => String(value)));
            return this.options.filter((opt) => selectedKeys.has(String(opt?.value)));
        },
    },
    watch: {
        selectedOptions(newVal) {
            if (JSON.stringify(newVal) !== JSON.stringify(this.modelValue)) {
                this.$emit("update:modelValue", newVal);
            }
        },
        modelValue(newVal) {
            if (JSON.stringify(newVal) !== JSON.stringify(this.selectedOptions)) {
                this.selectedOptions = [...newVal];
            }
        },
    },
    mounted() {
        document.addEventListener("click", this.handleClickOutside);
    },
    beforeUnmount() {
        document.removeEventListener("click", this.handleClickOutside);
    },
    methods: {
        toggleDropdown() {
            if (this.disabled) return;

            if (!this.isOpen) {
                this.$nextTick(() => {
                    const wrapper = this.$refs.dropdown as HTMLElement;
                    const rect = wrapper.getBoundingClientRect();

                    const dropdownHeight = 220;
                    const spaceBelow = window.innerHeight - rect.bottom;
                    const spaceAbove = rect.top;

                    let top: number;

                    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
                        // Not enough room below ➜ show above
                        top = rect.top + window.scrollY - dropdownHeight;
                    } else {
                        // Enough room below ➜ show below
                        top = rect.bottom + window.scrollY;
                    }

                    this.dropdownPosition = {
                        position: "absolute",
                        top: `${top}px`,
                        left: `${rect.left + window.scrollX}px`,
                        width: `${rect.width}px`,
                        zIndex: 9999,
                    };
                });
            }
            this.isOpen = !this.isOpen;
            if (!this.isOpen) {
                this.searchQuery = "";
            }
        },
        removeOption(value: string | number) {
            this.selectedOptions = this.selectedOptions?.filter((opt) => String(opt) !== String(value));
        },
        handleClickOutside(event: Event) {
            const wrapper = this.$refs.dropdown as HTMLElement;
            const teleported = this.$refs.teleportDropdown as HTMLElement;

            if (
                wrapper &&
                !wrapper.contains(event.target as Node) &&
                teleported &&
                !teleported.contains(event.target as Node)
            ) {
                this.isOpen = false;
            }
        },
        handleKeyDown(event: KeyboardEvent) {
            if (event.key === "Enter") {
                this.toggleDropdown();
            }
        },
    },
});
</script>

<style scoped>
.multi-select-dropdown {
    width: 100%;
}

.input-wrapper {
    display: flex;
    align-items: center;
    background-color: var(--neutral-athens);
    border-color: var(--neutral-manatee);
    border-width: 0 0 1px 0;
    border-style: solid;
    padding: 2px;
    border-radius: 2px;
    flex-wrap: wrap;
    cursor: pointer;
}

.disabled {
    opacity: 0.5;
    pointer-events: none;
}

.chips-container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    flex: 1;
    gap: 8px;
    width: calc(100% - 24px);
    max-height: calc(4 * 32px + 3 * 8px); /* 4 rows + 3 gaps */
    overflow-y: auto;
}

.chip {
    background: var(--accent-color3);
    border-radius: 4px;
    padding: 4px 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: calc(100% - 16px);
}

.chip-label{
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.chip-remove {
    margin-left: 6px;
    cursor: pointer;
}

.placeholder {
    color: var(--foreground-primary);
    opacity: 0.5;
    padding: 2px 11px;
}

.arrow {
    display: flex;
    cursor: pointer;
    user-select: none;
}

.dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    border: 1px solid var(--thin-border);
    background: var(--background-primary);
    z-index: 1000;
    max-height: 220px;
    overflow-y: auto;
    padding: 5px;
}

.search-input {
    width: 100%;
    padding: 5px;
    margin-bottom: 5px;
}

.dropdown-option {
    display: block;
    padding: 6px;
}

.no-results {
    padding: 8px;
    font-size: 12px;
    text-align: center;
}
</style>
